// ============================================================================
// Copy widgets — copy buttons on code/output blocks + one delegated handler
// ============================================================================
// Two pieces:
//   addCopyButtons(root) — post-render pass that wraps every <pre> lacking
//     a copy button in a .copy-wrap container with a hover-reveal .copy-btn.
//   initCopyButtons() — ONE delegated click handler for all .copy-btn and
//     .msg-copy buttons (survives streaming re-renders and DOM-window evict).
//
// The copied text comes from the button's data-copy-text attribute when
// present, else the sibling <pre>/<code> text inside its .copy-wrap, else
// (for .msg-copy buttons) the enclosing agent bubble's full text.

/** Copy text to the clipboard, with a textarea fallback for non-secure ctx. */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;opacity:0;pointer-events:none";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export function flashCopied(btn) {
  const prev = btn.textContent;
  btn.classList.add("copied");
  btn.textContent = "✓";
  setTimeout(() => {
    btn.classList.remove("copied");
    btn.textContent = prev;
  }, 1200);
}

/**
 * Wrap every <pre> under `root` (that isn't already wrapped) in a
 * .copy-wrap div with a .copy-btn. Idempotent — safe to call on every
 * streaming re-render; already-wrapped pre's carry data-copy-wired.
 */
export function addCopyButtons(root) {
  if (!root || !root.querySelectorAll) return;
  for (const pre of root.querySelectorAll("pre:not([data-copy-wired])")) {
    pre.dataset.copyWired = "1";
    const wrap = document.createElement("div");
    wrap.className = "copy-wrap";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "copy-btn";
    btn.title = "Copy";
    btn.textContent = "⧉";
    pre.parentNode.insertBefore(wrap, pre);
    wrap.appendChild(pre);
    wrap.appendChild(btn);
  }
}

/**
 * Install the global delegated click handler for copy buttons.
 * Call once at app init.
 */
export function initCopyButtons() {
  document.addEventListener("click", (e) => {
    const btn = e.target.closest?.(".copy-btn, .msg-copy");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    let text = btn.dataset?.copyText;
    if (text == null && btn.classList.contains("msg-copy")) {
      // Whole-agent-message copy: the bubble's rendered text.
      text = btn.closest(".msg-row")?.querySelector(".msg-bubble")?.innerText;
    }
    if (text == null) {
      const wrap = btn.closest(".copy-wrap");
      text = wrap?.querySelector("pre, code")?.innerText ?? "";
    }
    void copyText(text).then((ok) => { if (ok) flashCopied(btn); });
  });
}
